import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import Loader from "../../Components/GlobalLoader/Loader";
import { lookUpService } from "../../Services/common/lookup.service";
import TabProgressBar from "../../Shared/Tab/TabProgressBar";
import ItemsAttribute from "./ItemsAttribute";
import ItemsBasicInfo from "./ItemsBasicInfo";
import { Item, FormData, mapDataToTabs, attachments } from "./model/items.model";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../Layout/PageHeader";
import { createItem, getItemById, updateItem } from "./service/items.service";
import useClientStore, { Client } from "../../store/client/useClientStore";

export default function ItemsAddEdit() {
  const [loading, setLoading] = useState({
    ItemsBasicInfo: true,
    ItemsAttribute: true,
  });
  const [currentTab, setCurrentTab] = useState(0);
  const [lookupData, setLookupData] = useState({
    itemsBasicInfo: {
      itemsGroup: [],
      itemsCategories: [],
    },
    itemsAttribute: {
      fifo_method: [],
      popularity: [],
      fifo_date_method: [],
      serial_tracking: [],
    },
  });

  const [formData, setFormData] = useState<FormData>({
    ItemsBasicInfo: {},
    ItemsAttribute: {},
  });
  const [data, setData] = useState();
  const [itemCode, setItemCode] = useState<string>("");

  const navigation = useNavigate();
  const { getSelectedClient } = useClientStore();
  const { id } = useParams();
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [clientData, setClientData] = useState<Client | null>(null);

  const pageName = "Create Item";
  const tabGroup: any = {
    0: "ItemsBasicInfo",
    1: "ItemsAttribute",
  };
  const lookup_payload = {
    types: [
      "items.fifo_method",
      "items.popularity",
      "items.fifo_date_method",
      "items.serial_tracking",
    ],
  };
  useEffect(() => {
    const subscription = getSelectedClient().subscribe((client) => {
      setClientData(client);
    });

    return () => subscription.unsubscribe();
  }, [getSelectedClient]);
  const getClientData = async () => {
    if (clientData && Object.keys(clientData).length > 0 && clientData.code!=='all') {
      return clientData;
    } else {
      toast.error("Client code is required!", { duration: 2000 });
    }
  };

  const getItemCategoryAndItemGroup = () => {
    const response = Promise.all([
      lookUpService.getItemCategoryLookup().then((response) => {
        return response.data.data.results;
      }),
      lookUpService.getItemGroupLookup().then((response) => {
        return response.data.data.results;
      }),
    ]).catch((error) => {
      console.error("Error fetching lookups:", error);
    });

    return response;
  };

  async function fetchMultipleLookupData() {
    try {
      const response = await lookUpService.lookupByType(lookup_payload);

      if (response.data && response.data.data) {
        const groupedData = response.data.data.reduce(
          (acc: { [key: string]: Item[] }, item: Item) => {
            if (!acc[item.type]) {
              acc[item.type] = [];
            }
            acc[item.type].push(item);
            return acc;
          },
          {}
        );

        setLookupData((prev) => ({
          ...prev,
          itemsAttribute: {
            fifo_method: groupedData["items.fifo_method"],
            popularity: groupedData["items.popularity"],
            fifo_date_method: groupedData["items.fifo_date_method"],
            serial_tracking: groupedData["items.serial_tracking"],
          },
        }));
      } else {
        console.error("API response data is invalid.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }

  const getLookUpForSelectedTab = async (tab: number) => {
    try {
      const tabKey = tabGroup[tab];

      switch (tabKey) {
        case "ItemsBasicInfo":
          getItemCategoryAndItemGroup()
            .then((response: any) => {
              setLookupData((prev) => ({
                ...prev,
                itemsBasicInfo: {
                  itemsGroup: response[0],
                  itemsCategories: response[1],
                },
              }));
              setLoading((prev) => ({
                ...prev,
                ItemsBasicInfo: false,
              }));
            })
            .catch((error) => {
              console.error("Failed to fetch basic info lookups:", error);
              setLoading((prev) => ({ ...prev, basicInfo: false }));
            });
          break;
        case "ItemsAttribute":
          fetchMultipleLookupData()
            .then(() => {
              setLoading((prev) => ({
                ...prev,
                ItemsAttribute: false,
              }));
            })
            .catch((error) => {
              console.error("Failed to fetch basic info lookups:", error);
              setLoading((prev) => ({ ...prev, basicInfo: false }));
            });

          break;

        default:
          return;
      }
    } catch (error) {
      toast.error("Failed to Fetch Lookup Data");
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await getLookUpForSelectedTab(currentTab);
      if (id && !isDataLoaded) {
        await loadItemData(id);
        setIsDataLoaded(true);
      }
    };
    initializeData();
  }, [currentTab, id, isDataLoaded]);

  const loadItemData = async (id: string) => {
    try {
      const response = await getItemById(id);
      if (response.data) {
        setData(response.data);
        setItemCode(response.data.code);
        const mappedData = mapDataToTabs(response.data);
        setFormData((prev) => ({
          ...prev,
          ...mappedData,
        }));
      }
    } catch (error) {
      toast.error("Error loading item data");
      console.error(error);
    }
  };

  const handleFormData = async (data: any, tab: string) => {
    return new Promise<FormData>((resolve) => {
      setFormData((prev: FormData) => {
        const newData = {
          ...prev,
          [tab]: data,
        };
        resolve(newData);
        return newData;
      });
    });
  };

  const handleSubmit = async (submittedData: FormData) => {
    await getClientData();
    if (!clientData?.id || !clientData?.code || clientData.code==='all') {
      return;
    }
    try {
      // Prevent duplicate submissions
      if (loading.ItemsBasicInfo || loading.ItemsAttribute) {
        return;
      }
      let payload = {
        ...formData.ItemsBasicInfo,
        ...submittedData.ItemsAttribute,
        client: {
          code: clientData.code,
        },
      };
      if (payload.logo && payload.logo != null) {
        payload.logo = payload.logo;
      } else {
        payload.logo = {};
      }

      let response;
      if (id) {
        response = await updateItem(id, payload);
      } else {
        response = await createItem(payload);
      }
      if (response.status == 201 || response.status == 200) {
        const operationType = id? 'updated':'created';
        toast.success("Item "+operationType+" successfully!", { duration: 5000 });
        navigation("/account/client/items");
      } else if (response.status == 400) {
        if (response.error.detail.code) {
          toast.error(response.error.detail.code, { duration: 5000 });
        }
      } else {
        toast.error("An error occurred while submitting Item.", {
          duration: 2000,
        });
      }
    } catch (error) {
      toast.error("Error saving items");
      console.error(error);
    }
  };

  const handleNext = () => {
    const nextTab = Math.min(currentTab + 1, TabProgressBarData.length - 1);
    setCurrentTab(nextTab);
    const updatedCompletedTabs = [...completedTabs];
    updatedCompletedTabs[currentTab] = true;
    setCompletedTabs(updatedCompletedTabs);
  };

  const TabProgressBarData = [
    {
      step: 1,
      label: "Basic Information",
      content: loading.ItemsBasicInfo ? (
        <Loader />
      ) : (
        <ItemsBasicInfo
          handleNext={handleNext}
          lookupData={lookupData.itemsBasicInfo}
          onSubmit={async (data) => {
            await handleFormData(data, "ItemsBasicInfo");
          }}
          initialData={id ? formData.ItemsBasicInfo : undefined}
        />
      ),
    },
    {
      step: 2,
      label: "Attribute",
      content: loading.ItemsAttribute ? (
        <Loader />
      ) : (
        <ItemsAttribute
          handleNext={handleNext}
          lookupData={lookupData.itemsAttribute}
          onSubmit={async (data) => {
            const updatedFormData = await handleFormData(
              data,
              "ItemsAttribute"
            );
            handleSubmit(updatedFormData);
          }}
          initialData={id ? formData.ItemsAttribute : undefined}
        />
      ),
    },
  ];

  const [completedTabs, setCompletedTabs] = useState(
    Array(TabProgressBarData.length).fill(false)
  );
  return (
    <>
      <Toaster />
      <PageHeader
        addUrl="/account/client/items/add"
        listUrl="/account/client/items"
        gridUrl=""
        name={pageName}
        viewType={true}
      />
      <div className="clearfix"></div>

      <h5 className="text-start mb-3">
        {id ? `Edit Item (${itemCode})` : "Create Item"}
      </h5>
      <TabProgressBar
        tabs={TabProgressBarData}
        handleNext={handleNext}
        currentTab={currentTab}
      />
    </>
  );
}
